import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { BaseService } from '../../abstract/base.service';
import { AgremoMap } from '../data-models/agremo-map.model';
import { AgremoMapStatus } from '../data-models/agremo-map-status.model';
import { CreateMapDto } from '../request-models/create-map-dto.model';
import { AgremoMapRecheckResult } from '../response-models/agremo-map-recheck-result.model';
import { toCreateMapRequest } from '../utils/agremo-utils';
import { isRecord } from '../utils/is-record';
import { nullableString } from '../utils/wire-value-utils';

/**
 * Agremo maps (uploaded field imagery): `/user/agremo/maps[/:id[/recheck]]` (hub `user-agremo-controller.ts`).
 * The list is paged with `page` / `limit`; creating a map requires Pro (`pro_required`).
 * Status changes arrive on the `agremo-map:status` WebSocket event, which the field maps card applies.
 */
@Injectable({
  providedIn: 'root'
})
export class AgremoMapService extends BaseService<AgremoMap> {
  constructor() {
    super('user/agremo/maps', AgremoMap);
  }

  /** A field's maps (first page, newest first). Resolves `[]` on failure. */
  async loadMaps(fieldId: string): Promise<AgremoMap[]> {
    return this.listMaps({ fieldId }, 'Error loading maps');
  }

  /** A field's maps that are ready for analysis. Resolves `[]` on failure. */
  async getReadyMaps(fieldId: string): Promise<AgremoMap[]> {
    return this.listMaps({ fieldId, status: 'ready' }, 'Error loading ready maps');
  }

  /** Create a map (registers the uploaded imagery and queues processing). Rejects with an `ApiError`. */
  async createMap(dto: CreateMapDto): Promise<AgremoMap | null> {
    return firstValueFrom(this.create(toCreateMapRequest(dto)));
  }

  /** Delete a map. Rejects with an `ApiError`. */
  async deleteMap(mapId: string): Promise<void> {
    await firstValueFrom(this.delete(mapId));
  }

  /** Recheck a failed map's readiness in Agremo. Rejects with an `ApiError`. */
  async recheckMap(mapId: string): Promise<AgremoMapRecheckResult> {
    const read = (data: unknown) => ({ mapId: (isRecord(data) ? nullableString(data['mapId']) : undefined) ?? mapId });
    return firstValueFrom(this.request('POST', { id: mapId, path: 'recheck' }, read, {}));
  }

  /** Get human-readable map status text */
  getMapStatusText(status: AgremoMapStatus): string {
    switch (status) {
      case 'uploading': return 'Uploading';
      case 'processing': return 'Processing';
      case 'ready': return 'Ready';
      case 'failed': return 'Failed';
      default: return status;
    }
  }

  /** Get Bootstrap badge class for map status */
  getMapStatusBadgeClass(status: AgremoMapStatus): string {
    switch (status) {
      case 'uploading': return 'bg-info text-dark';
      case 'processing': return 'bg-warning text-dark';
      case 'ready': return 'bg-success';
      case 'failed': return 'bg-danger';
      default: return 'bg-secondary';
    }
  }

  private async listMaps(query: { fieldId: string; status?: AgremoMapStatus }, failure: string): Promise<AgremoMap[]> {
    try {
      const page = await firstValueFrom(this.getPage({ query }));
      return page.items;
    } catch (error: unknown) {
      console.error(`${failure}:`, ApiError.fromHttpError(error).message);
      return [];
    }
  }
}
